import { useRef, useEffect } from 'react'
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, Platform, Image } from 'react-native'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { colors, fonts, radius } from '../../lib/theme'
import { useTranslation } from 'react-i18next'
import { useThemeColors } from '../../lib/useThemeColors'
import RiderFace from '../RiderFace'

// ─── Web: dnd-kit — lazy init per evitare Platform.OS a livello modulo ──
let useSortable, CSS
let _uiDndInited = false

function initUiDnd() {
  if (_uiDndInited) return
  _uiDndInited = true
  if (Platform.OS === 'web') {
    try {
      const sortable = require('@dnd-kit/sortable')
      const utilities = require('@dnd-kit/utilities')
      useSortable = sortable.useSortable
      CSS = utilities.CSS
    } catch {}
  }
}

// ─── Alert ───────────────────────────────────────────────────────────
export function Alert({ type, children }) {
  const tc = useThemeColors()
  const styles = makeStyles(tc)
  const colorMap = {
    info:    { bg: 'rgba(59,130,246,.1)',  border: 'rgba(59,130,246,.3)',  color: '#7cb9ff' },
    error:   { bg: colors.errorBg,         border: tc.rosaBorder,      color: tc.rosaText },
    success: { bg: colors.successBg,       border: colors.successBorder,   color: colors.success },
  }
  const c = colorMap[type] ?? colorMap.info
  return (
    <View style={[styles.alert, { backgroundColor: c.bg, borderColor: c.border }]}>
      <Text style={[styles.alertText, { color: c.color }]}>{children}</Text>
    </View>
  )
}

// ─── EmptyState ──────────────────────────────────────────────────────
export function EmptyState({ icon, title, subtitle }) {
  const tc = useThemeColors()
  const styles = makeStyles(tc)
  return (
    <View style={styles.emptyState}>
      <Text style={styles.emptyIcon}>{icon}</Text>
      <Text style={styles.emptyTitle}>{title}</Text>
      {subtitle && <Text style={styles.emptySub}>{subtitle}</Text>}
    </View>
  )
}

// ─── NationalFlag — bandierina per id_national, con caso speciale per
// i corridori "Refugee" (REF su PCS → id_national = 'RF' nel DB): non
// essendo una vera nazione ISO non ha una bandiera reale su flagcdn,
// quindi mostriamo una bandiera bianca neutra invece di un'immagine rotta.
export function NationalFlag({ code, style }) {
  if (!code) return null
  const cc = code.trim().toUpperCase()
  if (cc === 'RF') {
    return <Text style={[{ fontSize: 14 }, style]}>🏳️</Text>
  }
  return (
    <Image
      source={{ uri: `https://flagcdn.com/w40/${cc.toLowerCase()}.png` }}
      style={[{ width: 18, height: 12, borderRadius: 2 }, style]}
    />
  )
}

// ─── BibTag — il dorsale del corridore (numero di gara, da map_start_list.bib): rettangolo bianco, numero nero in grassetto condensato e fascia
// rosa del brand in basso. size 'md' (22 px: scheda insight, modale), 'sm' (18 px), 'xs' (14 px: sovrapposto all'angolo del busto negli slot, cosi'
// non toglie spazio al nome). Senza numero non disegna nulla.
const BIB_SIZES = { md: { h: 22, minW: 32, font: 14, line: 16, strip: 4 }, sm: { h: 18, minW: 26, font: 12, line: 14, strip: 3 }, xs: { h: 15, minW: 22, font: 10, line: 12, strip: 3 } }
export function BibTag({ bib, size = 'md', style }) {
  const tc = useThemeColors()
  if (bib === null || bib === undefined || bib === '') return null
  const z = BIB_SIZES[size] ?? BIB_SIZES.md
  return (
    <View style={[{ height: z.h, minWidth: z.minW, paddingHorizontal: 3, borderRadius: 3, borderWidth: 1, borderColor: '#8E8E93', backgroundColor: '#FFFFFF', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }, style]}
      accessibilityLabel={`#${bib}`}>
      <Text style={{ fontFamily: fonts.condensedBold, fontSize: z.font, lineHeight: z.line, color: '#141414', marginTop: -1 }}>{bib}</Text>
      <View style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: z.strip, backgroundColor: tc.rosa }} />
    </View>
  )
}

// ─── RiderIdent — identita' del corridore su due livelli (slot Pick): sopra il nome per intero, sotto dorsale · sigla squadra · bandiera.
// Il dorsale per primo: e' cio' che si cerca nella start list e tiene le righe allineate. Nelle gare di nazionali niente sigla (e' "NAT").
export function riderTeamCode(label) {
  const m = label?.match(/\[([^\]]+)\]\s*$/)
  return m ? m[1] : null
}
export function RiderIdent({ rider, isNationalRace, nameStyle, size = 'sm', fullName = false, teamMode = 'code' }) {
  const tc = useThemeColors()
  const label = rider.label_new || rider.labelNew || rider.label
  const code = isNationalRace ? null : riderTeamCode(label)
  return (
    <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
      <RiderName name={fullName && rider.label ? stripTeamBracket(rider.label) : stripTeamBracket(label)} style={nameStyle} />
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
        <BibTag bib={rider.bib} size={size} />
        {code ? <Text style={{ fontSize: 12, fontFamily: fonts.condensedBold, color: tc.textMuted, flexShrink: 1 }} numberOfLines={1}>{(teamMode === 'long' && rider.teamName) || (teamMode !== 'code' && rider.teamShort) || code}</Text> : null}
        {rider.id_national ? <NationalFlag code={rider.id_national} /> : null}
      </View>
    </View>
  )
}

// ─── stripTeamBracket — rimuove il suffisso " [XXX] " (codice team,
// generico "NAT" nelle gare di nazionali) da un'etichetta tipo
// "EVENEPOEL Rem. [NAT]" → "EVENEPOEL Rem.": nelle gare di nazionali
// mostriamo solo bandiera + nome, il codice team è ridondante/fuorviante.
export function stripTeamBracket(label) {
  if (!label) return label
  return label.replace(/\s*\[[^\]]*\]\s*$/, '').trim()
}

// ─── RiderName (Nuovo Componente Formattato) ─────────────────────────
export function RiderName({ name, style }) {
  if (!name) return <Text style={style}>—</Text>

  // Divide la stringa (es: "POGAČAR Tadej") assumendo che la prima parola sia il Cognome
  const parts = name.trim().split(/\s+/)
  const surname = parts[0]
  const firstName = parts.slice(1).join(' ')

  return (
    <Text style={style} numberOfLines={1}>
      <Text style={{ fontWeight: '800' }}>{surname}</Text>
      <Text style={{ fontWeight: '400', opacity: 0.85 }}> {firstName}</Text>
    </Text>
  )
}

// ─── Insight del corridore: controlli (bersagli da almeno 44 pt, distanziati dagli altri) ───
// Cella a destra della lista di selezione: icona + quante volte l'hai scelto, separata dalla riga da una linea sottile,
// cosi' toccare il nome (scegliere) e toccare le info (confrontare) non si confondono.
export function InsightCell({ insight, onPress, name }) {
  const tc = useThemeColors()
  const { t } = useTranslation()
  return (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.6}
      style={{ width: 56, alignSelf: 'stretch', minHeight: 52, alignItems: 'center', justifyContent: 'center', borderLeftWidth: StyleSheet.hairlineWidth, borderLeftColor: tc.border, gap: 1 }}
      accessibilityRole="button"
      accessibilityLabel={t('insight.open_a11y', { name })}
    >
      <MaterialCommunityIcons name="information-outline" size={22} color={tc.rosaText} />
      {insight?.mine_n > 0 && <Text style={{ fontSize: 10, fontFamily: fonts.condensedBold, color: tc.textMuted }}>{insight.mine_n}×</Text>}
    </TouchableOpacity>
  )
}

// ─── SortableRider Web (dnd-kit) ──────────────────────────────────────
function SortableRiderWeb({ rider, index, isCompact, onRemove, isNationalRace }) {
  initUiDnd()
  const tc = useThemeColors()
  const styles = makeStyles(tc)
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: rider.value })
  return (
    <View
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={[
        styles.riderRow,
        isCompact && styles.riderRowCompact,
        isDragging && styles.riderRowActive,
        { transform: CSS?.Transform?.toString(transform), transition, cursor: 'grab', userSelect: 'none' },
      ]}
    >
      <View style={styles.handle}>
        <View style={styles.handleLine} />
        <View style={styles.handleLine} />
        <View style={styles.handleLine} />
      </View>
      <Text style={[styles.slotNum, isCompact && styles.slotNumCompact]}>{index + 1}°</Text>

      {/* 1. BUSTO — volto + maglia della squadra (nelle gare di nazionali maglia generica: niente maglia team, solo bandiera) */}
      <RiderFace avatar={rider.avatar} kit={isNationalRace ? null : rider.kit} size={isCompact ? 40 : 46} shape="bust" />

      {/* 2. NOME su due livelli: nome / dorsale · squadra · bandiera */}
      <RiderIdent rider={rider} isNationalRace={isNationalRace} fullName teamMode="short" nameStyle={[styles.riderName, isCompact && styles.riderNameCompact]} size={isCompact ? 'xs' : 'sm'} />

      <TouchableOpacity
        style={styles.removeBtn}
        onPress={() => onRemove(index)}
        onPointerDown={(e) => e.stopPropagation()}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
      >
        <Text style={styles.removeBtnText}>−</Text>
      </TouchableOpacity>
    </View>
  )
}

// ─── SortableRider Mobile (per DraggableFlatList) ────────────────────
function SortableRiderMobile({ rider, index, isCompact, onRemove, drag, isActive, isNationalRace }) {
  const tc = useThemeColors()
  const { t } = useTranslation()
  const styles = makeStyles(tc)
  return (
    <TouchableOpacity
      onLongPress={drag}
      delayLongPress={200}
      activeOpacity={1}
      style={[
        styles.riderRow,
        isCompact && styles.riderRowCompact,
        isActive && styles.riderRowActive,
      ]}
    >
      <View style={styles.handle}>
        <View style={styles.handleLine} />
        <View style={styles.handleLine} />
        <View style={styles.handleLine} />
      </View>
      <Text style={[styles.slotNum, isCompact && styles.slotNumCompact]}>{index + 1}°</Text>

      {/* 1. BUSTO — volto + maglia della squadra (nelle gare di nazionali maglia generica: niente maglia team, solo bandiera) */}
      <RiderFace avatar={rider.avatar} kit={isNationalRace ? null : rider.kit} size={isCompact ? 40 : 46} shape="bust" />

      {/* 2. NOME su due livelli: nome / dorsale · squadra · bandiera */}
      <RiderIdent rider={rider} isNationalRace={isNationalRace} fullName teamMode="short" nameStyle={[styles.riderName, isCompact && styles.riderNameCompact]} size={isCompact ? 'xs' : 'sm'} />

      <TouchableOpacity
        style={styles.removeHit}
        onPress={() => onRemove(index)}
        accessibilityRole="button"
        accessibilityLabel={t('pick.draft_cancel')}
      >
        <View style={styles.removeBtn}>
          <Text style={styles.removeBtnText}>−</Text>
        </View>
      </TouchableOpacity>
    </TouchableOpacity>
  )
}

export function SortableRider(props) {
  if (Platform.OS === 'web') return <SortableRiderWeb {...props} />
  return <SortableRiderMobile {...props} />
}

// ─── PillBar ─────────────────────────────────────────────────────────
// NOTA: questo PillBar NON è quello effettivamente usato dal tab Pick
// (quello vero, con supporto al collasso/pallino verde, è in
// components/PillBar.jsx e viene importato da app/Pick/index.jsx).
// Questo è rimasto qui per compatibilità con altri eventuali usi interni
// a questo file, versione semplice senza collasso.
export function PillBar({ items, selectedValue, onSelect, large = false }) {
  const tc = useThemeColors()
  const styles = makeStyles(tc)
  const scrollRef = useRef(null)
  const itemRefs = useRef({})

  useEffect(() => {
    if (selectedValue == null || !scrollRef.current) return
    const timer = setTimeout(() => {
      const ref = itemRefs.current[selectedValue]
      if (ref) {
        ref.measureLayout(
          scrollRef.current.getScrollableNode?.() ?? scrollRef.current,
          (x) => { scrollRef.current.scrollTo({ x: x - 60, animated: true }) },
          () => {}
        )
      }
    }, 150)
    return () => clearTimeout(timer)
  }, [selectedValue])

  if (!items?.length) return null
  return (
    <ScrollView ref={scrollRef} horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.pillBarContainer}>
      {items.map(item => {
        const isActive = item.value === selectedValue
        return (
          <TouchableOpacity
            key={item.value}
            ref={(r) => { if (r) itemRefs.current[item.value] = r }}
            onPress={() => onSelect(item)}
            style={[styles.pill, large && styles.pillLarge, isActive && styles.pillActive, item.isLive && styles.pillLive]}
          >
            {item.isLive && <View style={styles.liveDot} />}
            <Text style={[styles.pillText, large && styles.pillTextLarge, isActive && styles.pillTextActive]}>
              {item.label}
            </Text>
          </TouchableOpacity>
        )
      })}
    </ScrollView>
  )
}

// ─── Utilities ───────────────────────────────────────────────────────
export const formatRiderNameParts = (name) => {
  if (!name) return { top: '—', bottom: '' }
  const parts = name.trim().split(/\s+/)
  if (parts.length < 2) return { top: name.toUpperCase(), bottom: '' }
  const surname = parts[parts.length - 1].toUpperCase()
  const firstName = parts.slice(0, parts.length - 1).join(' ')
  return { top: surname, bottom: firstName }
}

const makeStyles = (tc) => StyleSheet.create({
  alert: { borderWidth: 1, borderRadius: radius.md, padding: 10, paddingHorizontal: 14, marginBottom: 12 },
  alertText: { fontSize: 13, lineHeight: 18 },
  emptyState: { alignItems: 'center', padding: 32 },
  emptyIcon: { fontSize: 32, marginBottom: 12 },
  emptyTitle: { fontSize: 15, fontFamily: fonts.condensedBold, color: tc.text, textAlign: 'center', marginBottom: 6 },
  emptySub: { fontSize: 13, color: tc.textMuted, textAlign: 'center', lineHeight: 18 },
  riderRow: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: tc.bgCard, borderWidth: 1, borderColor: tc.rosaBorder, borderRadius: radius.lg, padding: 14, paddingHorizontal: 12, marginBottom: 8 },
  riderRowCompact: { padding: 12 },
  riderRowActive: { opacity: 0.7, borderColor: tc.rosa },
  handle: { flexDirection: 'column', gap: 3, opacity: 0.35, paddingRight: 2 },
  handleLine: { width: 14, height: 1.5, backgroundColor: tc.textMuted, borderRadius: 1 },
  slotNum: { fontFamily: fonts.condensedBold, fontSize: 18, color: tc.rosaText, width: 24, flexShrink: 0 },
  slotNumCompact: { fontSize: 16 },
  riderName: { flex: 1, fontSize: 14, color: tc.text },
  riderNameCompact: { fontSize: 13 },
  removeHit: { minWidth: 44, minHeight: 44, marginVertical: -8, marginRight: -8, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  removeBtn: { backgroundColor: tc.rosaSubtle, borderRadius: 14, width: 28, height: 28, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  removeBtnText: { color: tc.rosaText, fontSize: 18, lineHeight: 20 },
  pillBarContainer: { paddingHorizontal: 16, gap: 8, paddingVertical: 10 },
  pill: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: radius.full, backgroundColor: tc.bgCard2, borderWidth: 1, borderColor: tc.border, flexDirection: 'row', alignItems: 'center', gap: 6 },
  pillLarge: { paddingHorizontal: 16, paddingVertical: 9 },
  pillActive: { backgroundColor: tc.rosa, borderColor: tc.rosa },
  pillLive: { borderColor: tc.rosa },
  pillText: { fontSize: 13, fontFamily: fonts.condensedMedium, color: tc.textMuted },
  pillTextLarge: { fontSize: 15, letterSpacing: 0.5 },
  pillTextActive: { color: tc.brandText, fontFamily: fonts.condensedBold },
  liveDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: tc.rosa },
})